import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNotification } from "../../contexts/NotificationContext";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Form,
  FormGroup,
  Input,
  Row,
  Col,
  Label,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { useCreateTenantUser } from "api/users/service";
import { useGetRoles } from "api/roles/service";
import { formatPhoneNumber, validatePhoneNumber } from "../../utils/phoneUtils";
import { getActiveColorStyle } from "../../utils/colorUtils";
import Select from "components/Select";

const initialForm = {
  email: "",
  alt_email: "",
  password: "",
  full_name: "",
  tenant_name: "",
  tenant_status: true,
  salutation: "",
  suffix: "",
  title: "",
  work_phone: "",
  home_phone: "",
  cell_phone: "",
  fax: "",
};

const suffixOptions = [
  "",
  "Jr.",
  "Sr.",
  "I",
  "II",
  "III",
  "IV",
  "V",
];
const salutationOptions = [
  "",
  "Mr.",
  "Mrs.",
  "Ms.",
  "Dr.",
  "Prof.",
  "Rev.",
  "Sir",
  "Madam",
];

const UserAdd = ({ activeColor = "secondary" }) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const navigate = useNavigate();
  const createTenantUserMutation = useCreateTenantUser();
  const { data: rolesData, isLoading: rolesLoading } = useGetRoles();
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [selectedRole, setSelectedRole] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Apply phone formatting for phone fields
    let formattedValue = value;
    if (name === 'work_phone' || name === 'home_phone' || name === 'cell_phone') {
      formattedValue = formatPhoneNumber(value);
    }

    setFormData((prev) => ({ ...prev, [name]: formattedValue }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const missingFields = [];

    if (!formData.email) {
      newErrors.email = t('userAdd.errors.emailRequired');
      missingFields.push(t('userAdd.labels.email'));
    }
    if (!formData.full_name) {
      newErrors.full_name = t('userAdd.errors.fullNameRequired');
      missingFields.push(t('userAdd.labels.fullName'));
    }
    if (!selectedRole) {
      newErrors.user_role = t('userAdd.errors.roleRequired');
      missingFields.push(t('userAdd.labels.userRole'));
    }

    // Phone validation using utility
    const phoneError = validatePhoneNumber(formData.work_phone);
    if (phoneError) {
      newErrors.work_phone = phoneError;
      missingFields.push(t('userAdd.labels.workPhone'));
    }

    if (!formData.password) {
      newErrors.password = t('userAdd.errors.passwordRequired');
      missingFields.push(t('userAdd.labels.password'));
    } else if (formData.password.length < 6) {
      newErrors.password = t('userAdd.errors.passwordMinLength');
      missingFields.push(t('userAdd.labels.password'));
    }

    setErrors(newErrors);

    // Show notification if there are errors
    if (Object.keys(newErrors).length > 0) {
      const fieldsList = missingFields.join(', ');
      showNotification(
        'danger',
        `${t('userAdd.requiredFieldsMissing')}: ${fieldsList}`
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const roleEntity = rolesData?.results?.find((r) => r.documentId === selectedRole);
    if (!roleEntity?.id) {
      setErrors((prev) => ({ ...prev, user_role: "Invalid role selection" }));
      return;
    }

    const payload = {
      ...formData,
      user: { user_role: roleEntity.id },
    };

    createTenantUserMutation.mutate(payload);
  };

  return (
    <div className="content">
      <Row className="justify-content-center">
        <Col md="12" className="rounded-0">
          <Card>
            <CardHeader>
              <h5 className="title">{t('userAdd.title')}</h5>
            </CardHeader>
            <Form onSubmit={handleSubmit}>
              <CardBody>
                <Row>
                  <Col md="6">
                    <div className="mb-1">
                      <FormGroup>
                        <Label>{t('userAdd.labels.email')} <span style={{ color: 'red' }}>*</span></Label>
                        <Input
                          style={{ borderRadius: '0px', fontSize: '14px' }}
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          invalid={!!errors.email}
                          placeholder={t('userAdd.placeholders.email')}
                          disabled={createTenantUserMutation.isPending}
                        />
                        {errors.email && <div className="text-danger small">{errors.email}</div>}
                      </FormGroup>
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-1">
                      <FormGroup>
                        <Label>{t('userAdd.labels.password')} <span style={{ color: 'red' }}>*</span></Label>
                        <Input
                          style={{ borderRadius: '0px', fontSize: '14px' }}
                          name="password"
                          type="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          invalid={!!errors.password}
                          placeholder={t('userAdd.placeholders.password')}
                          disabled={createTenantUserMutation.isPending}
                        />
                        {errors.password && <div className="text-danger small">{errors.password}</div>}
                      </FormGroup>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <div className="mb-1">
                      <FormGroup>
                        <Label>{t('userAdd.labels.userRole')} <span style={{ color: 'red' }}>*</span></Label>
                        <Select
                          activeColor={activeColor}
                          className="react-select"
                          placeholder={t('userAdd.placeholders.selectRole')}
                          name="user_role"
                          isSearchable
                          closeMenuOnSelect={true}
                          value={rolesData?.results?.find(role => role.documentId === selectedRole) ?
                            { value: selectedRole, label: rolesData.results.find(role => role.documentId === selectedRole)?.name } : null}
                          onChange={(option) => {
                            setSelectedRole(option?.value || "");
                            if (errors.user_role) {
                              setErrors((prev) => ({ ...prev, user_role: undefined }));
                            }
                          }}
                          options={rolesData?.results?.map(role => ({ value: role.documentId, label: role.name })) || []}
                          isDisabled={createTenantUserMutation.isPending || rolesLoading}
                          isClearable
                        />
                        {errors.user_role && (
                          <div className="text-danger small">{errors.user_role}</div>
                        )}
                      </FormGroup>
                    </div>
                  </Col>
                </Row>
                <Row>


                  <Col md="6">
                    <div className="mb-1">
                      <FormGroup>
                        <Label>{t('userAdd.labels.fullName')} <span style={{ color: 'red' }}>*</span></Label>
                        <Input
                          style={{ borderRadius: '0px', fontSize: '14px' }}
                          name="full_name"
                          value={formData.full_name}
                          onChange={handleInputChange}
                          invalid={!!errors.full_name}
                          placeholder={t('userAdd.placeholders.fullName')}
                          disabled={createTenantUserMutation.isPending}
                        />
                        {errors.full_name && <div className="text-danger small">{errors.full_name}</div>}
                      </FormGroup>
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-1">
                      <FormGroup>
                        <Label>{t('userAdd.labels.title')}</Label>
                        <Input
                          style={{ borderRadius: '0px', fontSize: '14px' }}

                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          placeholder={t('userAdd.placeholders.title')}
                          disabled={createTenantUserMutation.isPending}
                        />
                      </FormGroup>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <div className="mb-1">
                      <FormGroup>
                        <Label>{t('userAdd.labels.salutation')}</Label>
                        <Select
                          activeColor={activeColor}
                          className="react-select"
                          placeholder={t('userAdd.placeholders.select')}
                          name="salutation"
                          isSearchable={false}
                          closeMenuOnSelect={true}
                          value={salutationOptions.find(opt => opt === formData.salutation) ?
                            { value: formData.salutation, label: formData.salutation || t('userAdd.placeholders.select') } : null}
                          onChange={(option) => {
                            const value = option?.value || "";
                            handleInputChange({ target: { name: "salutation", value } });
                          }}
                          options={salutationOptions.map(opt => ({ value: opt, label: opt || t('userAdd.placeholders.select') }))}
                          isDisabled={createTenantUserMutation.isPending}
                          isClearable
                        />
                      </FormGroup>
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="mb-1">
                      <FormGroup>
                        <Label>{t('userAdd.labels.suffix')}</Label>
                        <Select
                          activeColor={activeColor}
                          className="react-select"
                          placeholder={t('userAdd.placeholders.select')}
                          name="suffix"
                          isSearchable={false}
                          closeMenuOnSelect={true}
                          value={suffixOptions.find(opt => opt === formData.suffix) ?
                            { value: formData.suffix, label: formData.suffix || t('userAdd.placeholders.select') } : null}
                          onChange={(option) => {
                            const value = option?.value || "";
                            handleInputChange({ target: { name: "suffix", value } });
                          }}
                          options={suffixOptions.map(opt => ({ value: opt, label: opt || t('userAdd.placeholders.select') }))}
                          isDisabled={createTenantUserMutation.isPending}
                          isClearable
                        />
                      </FormGroup>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md="4">
                    <div className="mb-1">
                      <FormGroup>
                        <Label>{t('userAdd.labels.workPhone')} <span style={{ color: 'red' }}>*</span></Label>
                        <Input
                          style={{ borderRadius: '0px', fontSize: '14px' }}
                          name="work_phone"
                          value={formData.work_phone}
                          onChange={handleInputChange}
                          invalid={!!errors.work_phone}
                          placeholder={t('userAdd.placeholders.workPhone')}
                          disabled={createTenantUserMutation.isPending}
                        />
                        {errors.work_phone && <div className="text-danger small">{errors.work_phone}</div>}
                      </FormGroup>
                    </div>
                  </Col>
                  <Col md="4">
                    <div className="mb-1">
                      <FormGroup>
                        <Label>{t('userAdd.labels.homePhone')}</Label>
                        <Input
                          style={{ borderRadius: '0px', fontSize: '14px' }}

                          name="home_phone"
                          value={formData.home_phone}
                          onChange={handleInputChange}
                          placeholder={t('userAdd.placeholders.homePhone')}
                          disabled={createTenantUserMutation.isPending}
                        />
                      </FormGroup>
                    </div>
                  </Col>
                  <Col md="4">
                    <div className="mb-1">
                      <FormGroup>
                        <Label>{t('userAdd.labels.cellPhone')}</Label>
                        <Input
                          style={{ borderRadius: '0px', fontSize: '14px' }}

                          name="cell_phone"
                          value={formData.cell_phone}
                          onChange={handleInputChange}
                          placeholder={t('userAdd.placeholders.cellPhone')}
                          disabled={createTenantUserMutation.isPending}
                        />
                      </FormGroup>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <div className="mb-1">
                      <FormGroup>
                        <Label>{t('userAdd.labels.fax')}</Label>
                        <Input
                          style={{ borderRadius: '0px', fontSize: '14px' }}

                          name="fax"
                          value={formData.fax}
                          onChange={handleInputChange}
                          placeholder={t('userAdd.placeholders.fax')}
                          disabled={createTenantUserMutation.isPending}
                        />
                      </FormGroup>
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-1">
                      <FormGroup>
                        <Label>{t('userAdd.labels.altEmail')}</Label>
                        <Input
                          style={{ borderRadius: '0px', fontSize: '14px' }}

                          name="alt_email"
                          value={formData.alt_email}
                          onChange={handleInputChange}
                          placeholder={t('userAdd.placeholders.altEmail')}
                          disabled={createTenantUserMutation.isPending}
                        />
                      </FormGroup>
                    </div>
                  </Col>
                </Row>
              </CardBody>
              <CardFooter className="d-flex justify-content-end gap-2">
                <Button
                  outline
                  onClick={() => navigate(-1)}
                  color="secondary"
                  disabled={createTenantUserMutation.isPending}
                >
                  {t('userAdd.buttons.cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={createTenantUserMutation.isPending}
                  style={{
                    ...activeColorStyle,
                    border: 'none',
                    color: 'white',
                    boxShadow: 'none',
                    opacity: 1,
                    fontWeight: '600'
                  }}
                >
                  {createTenantUserMutation.isPending ? t('userAdd.buttons.saving') : t('userAdd.buttons.save')}
                </Button>
              </CardFooter>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default UserAdd; 